import _ from "lodash";
import { colors } from "Constants";
import { getIsEvaluationDone } from "ClassRoom/modules/utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getArchivedText } from "Utils";
import { getUserArchivalStatus } from "modules/CommonSelectors";

const colorNew = [
  colors.yellow700,
  colors.yellow600,
  colors.yellow500,
  colors.yellow400,
  colors.yellow300,
  colors.yellow200,
  colors.yellow100,
];

const getStudentScoresMemoize = _.memoize(
  params => getStudentScores(params),
  params => JSON.stringify(params)
);

const getStudentScores = ({ assignedStudents, t }) => {
  const studentScores = [];

  _.forEach(assignedStudents, ({ ibPYPElementRatings, student }) => {
    if (ibPYPElementRatings) {
      const studentScore = _.get(
        _.find(
          ibPYPElementRatings,
          ibPYPElementRating => ibPYPElementRating.ibPYPElementType === "SCORE"
        ),
        "value"
      );
      const isStudentArchived = getUserArchivalStatus(student);
      const formattedUserName = getFormattedUserName({
        user: student,
      });
      const studentName = getArchivedText({
        name: formattedUserName,
        isArchived: isStudentArchived,
        t,
      });

      if (studentScore) {
        studentScores.push({
          studentMark: _.toNumber(studentScore),
          studentName,
        });
      }
    }
  });

  return { studentsMark: studentScores };
};

/**
 * 1. [Sort]: By students
 * 2. [Loop]: till max length
 *    2.1 [Nested Loop]: loop to update fill
 */
const getDataWithFill = ({ data }) => {
  if (_.isEmpty(data)) return [];
  const color = colorNew;

  const sortedData = _.sortBy(data, o => o?.students);

  const length = _.size(sortedData);
  let colorIndex = 0;
  let dataWithFill = [...data];

  for (let i = 0; i < length; i++) {
    const currentPointer = sortedData[i];
    const nextPointer = sortedData[i + 1];

    dataWithFill = _.map(dataWithFill, o => {
      if (o.name === currentPointer?.name) {
        return {
          ...o,
          fill: color[1],
        };
      } else {
        return o;
      }
    });

    if (currentPointer?.students !== nextPointer?.students) colorIndex++;
  }

  return {
    dataWithFill,
  };
};

/**
 * Description: This function returns min and max score of particular bar
 * Sample Input: index = 3, maxScore = 100, barCount = 10
 * Sample Output: max = 30, min = 20, mid = 25
 */
const getMarksRange = ({
  index,
  maxScore,
  remainingMarks,
  individualMarksRange,
}) => {
  const max = Math.min((individualMarksRange + 1) * index, maxScore);

  const min = (individualMarksRange + 1) * (index - 1);

  const updatedRemainingMarks = remainingMarks - max + min;

  return {
    min: min == 0 ? 0 : min + 1,
    max: updatedRemainingMarks === 0 ? max : max + 0.99, // adding 0.99 because we allow max 2 digits after decimal in score
    updatedRemainingMarks: remainingMarks - max + min,
  };
};

/**
 *
 * @param {Array} assignedStudents
 * @param {String || Number} maxScore Ex: 100
 * @param {String || Number} barCount Ex: 10
 * @returns {Object} Structure -> {data, ticks}
 */
const getBarChartProps = ({ assignedStudents, maxScore, t }) => {
  const { studentsMark } = getStudentScoresMemoize({
    assignedStudents,
    t,
  });

  if (_.isEmpty(studentsMark) || !maxScore) return {};

  const data = [];
  // const ticks = [];

  let remainingMarks = maxScore;
  let i = 1;

  const individualMarksRange =
    maxScore % 10 === 0 && maxScore > 10
      ? parseInt(maxScore / 10) - 1
      : parseInt(maxScore / 10);

  while (remainingMarks > 0) {
    const { min, max, updatedRemainingMarks } = getMarksRange({
      index: i,
      maxScore,
      remainingMarks,
      individualMarksRange,
    });

    remainingMarks = updatedRemainingMarks;

    // Logic for displaying ticks(Data points) on the x-axis
    // ticks.push(parseFloat(min.toFixed(2)));
    // if (i === barCount) {
    //   ticks.push(parseFloat(max.toFixed(2)));
    // }

    let noOfStudentsInRange = 0;
    const studentList = [];

    _.forEach(studentsMark, ({ studentMark, studentName }) => {
      if (
        (studentMark >= min && studentMark <= max) ||
        (i === 1 && studentMark === min) || // if mark is 0 (exception)
        (min == max && studentMark == min)
      ) {
        noOfStudentsInRange++;
        studentList.push({ studentMark, studentName });
      }
    });

    let marksMidRange = `${min} ${t("project:or_higher")}`;
    if (updatedRemainingMarks === 0) {
      marksMidRange = min === max ? min : `${min}-${max}`;
    }

    data.push({
      name: `BAR_${i}`,
      students: noOfStudentsInRange,
      marksMidRange,
      studentList,
    });

    i++;
  }

  const { dataWithFill } = getDataWithFill({ data });

  return {
    data: dataWithFill,
  };
};

const getBarChartPropsMemoize = _.memoize(
  params => getBarChartProps(params),
  params => JSON.stringify(params)
);

const getFilteredStudentListMemoize = _.memoize(
  params => getFilteredStudentList(params),
  params => JSON.stringify(params)
);

const getFilteredStudentList = ({
  assignedStudents,
  responseType,
  isScoreEnabled,
  toolId,
  t,
}) => {
  const total = _.size(
    _.filter(assignedStudents, ({ isEvaluationApplicable }) => {
      return isEvaluationApplicable;
    })
  );

  if (isScoreEnabled) {
    const { studentsMark } = getStudentScoresMemoize({
      assignedStudents,
      t,
    });

    const doneStudentScoresLength = _.size(studentsMark);

    return {
      total,
      done: doneStudentScoresLength,
      pending: total - doneStudentScoresLength,
    };
  }

  let done = 0;
  if (responseType == "teacherResponse") {
    done = _.size(
      _.filter(assignedStudents, ({ isEvaluationApplicable, isEvaluated }) => {
        return isEvaluationApplicable && isEvaluated;
      })
    );
  } else {
    done = _.size(
      _.filter(
        assignedStudents,
        ({ isEvaluationApplicable, toolResponses }) => {
          const isEvaluationDone = getIsEvaluationDone({
            toolResponses,
            assessmentToolId: toolId,
          });

          return isEvaluationApplicable && isEvaluationDone;
        }
      )
    );
  }
  return {
    total,
    done,
    pending: total - done,
  };
};

const getScoreDetailMemoize = _.memoize(
  params => getScoreDetail(params),
  params => JSON.stringify(params)
);

const getScoreDetail = ({ assignedStudents, id, t }) => {
  const { studentsMark } = getStudentScoresMemoize({
    assignedStudents,
    t,
  });

  const studentScores = _.map(studentsMark, ({ studentMark }) => studentMark);

  if (!_.size(studentsMark)) {
    return "NA";
  }
  switch (id) {
    case "AVERAGE":
      return parseFloat(
        (_.sum(studentScores) / _.size(studentScores)).toFixed(2)
      );

    case "HIGHEST":
      return _.max(studentScores);
    case "LOWEST":
      return _.min(studentScores);
  }
};

const getPath = (x, y, width, height, cornerRadius = 8) => {
  cornerRadius = Math.min(cornerRadius, height / 2, width / 2);

  return `M${x},${y + cornerRadius}
          C${x},${y + cornerRadius / 2} ${x + cornerRadius / 2},${y} ${
    x + cornerRadius
  },${y}
          H${x + width - cornerRadius}
          C${x + width - cornerRadius / 2},${y} ${x + width},${
    y + cornerRadius / 2
  } ${x + width},${y + cornerRadius}
          V${y + height}
          H${x}
          V${y + cornerRadius}
          Z`;
};

export {
  getBarChartPropsMemoize,
  getScoreDetailMemoize,
  getFilteredStudentListMemoize,
  getPath,
};
